import { Platform, TextStyle } from 'react-native';

const InterFonts = {
  regular: 'Inter-Regular',
  medium: 'Inter-Medium',
  semibold: 'Inter-SemiBold',
  bold: 'Inter-Bold',
  extraBold: 'Inter-ExtraBold',
  black: 'Inter-Black',
  italic: 'Inter-Italic',
  thin: 'Inter-Thin',
  extraLight: 'Inter-ExtraLight',
  light: 'Inter-Light',
  thinItalic: 'Inter-ThinItalic',
  extraLightItalic: 'Inter-ExtraLightItalic',
  lightItalic: 'Inter-LightItalic',
  mediumItalic: 'Inter-MediumItalic',
  semiboldItalic: 'Inter-SemiBoldItalic',
  boldItalic: 'Inter-BoldItalic',
  extraBoldItalic: 'Inter-ExtraBoldItalic',
  blackItalic: 'Inter-BlackItalic',
} as const;

const MontserratFonts = {
  medium: 'Montserrat-Medium',
  semibold: 'Montserrat-SemiBold',
  bold: 'Montserrat-Bold',
  extraBold: 'Montserrat-ExtraBold',
  black: 'Montserrat-Black',
  italic: 'Montserrat-Italic',
  thin: 'Montserrat-Thin',
  extraLight: 'Montserrat-ExtraLight',
  light: 'Montserrat-Light',
  thinItalic: 'Montserrat-ThinItalic',
  extraLightItalic: 'Montserrat-ExtraLightItalic',
  lightItalic: 'Montserrat-LightItalic',
  mediumItalic: 'Montserrat-MediumItalic',
  semiboldItalic: 'Montserrat-SemiBoldItalic',
  boldItalic: 'Montserrat-BoldItalic',
  extraBoldItalic: 'Montserrat-ExtraBoldItalic',
  blackItalic: 'Montserrat-BlackItalic',
  regular: 'Montserrat-Regular',
} as const;

// Open Sans ships as split families per weight (Open Sans / Open Sans SemiBold / Open Sans
// Medium / Open Sans Light / Open Sans ExtraBold), so unlike Inter/Montserrat the iOS
// family-name + fontWeight approach can't reach SemiBold/Medium. We resolve to the exact
// per-face PostScript name on every platform — see `resolveFontFamily`.
const OpenSansFonts = {
  light: 'OpenSans-Light',
  regular: 'OpenSans-Regular',
  medium: 'OpenSans-Medium',
  semibold: 'OpenSans-SemiBold',
  bold: 'OpenSans-Bold',
  extraBold: 'OpenSans-ExtraBold',
  lightItalic: 'OpenSans-LightItalic',
  italic: 'OpenSans-Italic',
  mediumItalic: 'OpenSans-MediumItalic',
  semiboldItalic: 'OpenSans-SemiBoldItalic',
  boldItalic: 'OpenSans-BoldItalic',
  extraBoldItalic: 'OpenSans-ExtraBoldItalic',
} as const;

export const fontFamilies = {
  inter: 'Inter',
  montserrat: 'Montserrat',
  openSans: 'OpenSans',
} as const;

type FontWeight = TextStyle['fontWeight'];
type FontStyle = TextStyle['fontStyle'];
export type FontFamily = (typeof fontFamilies)[keyof typeof fontFamilies];

const parseFontWeight = (fontWeight: FontWeight): number | undefined => {
  if (fontWeight == null) return undefined;
  if (typeof fontWeight === 'number') return fontWeight;
  if (fontWeight === 'normal') return 400;
  if (fontWeight === 'bold') return 700;
  const parsed = Number.parseInt(fontWeight, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const resolveFontFamily = ({
  fontFamily,
  fontWeight,
  fontStyle,
}: {
  fontFamily: FontFamily;
  fontWeight: FontWeight;
  fontStyle: FontStyle;
}): { fontFamily: string } => {
  const weight = parseFontWeight(fontWeight) ?? 400;
  const isItalic = fontStyle === 'italic';

  // Open Sans must resolve to a per-face PostScript name on every platform (incl. iOS)
  // because its weights live in separate families. Open Sans tops out at ExtraBold (800)
  // and bottoms out at Light (300) — no Black/Thin faces.
  if (fontFamily.startsWith('OpenSans')) {
    if (isItalic) {
      if (weight >= 800) return { fontFamily: OpenSansFonts.extraBoldItalic };
      if (weight >= 700) return { fontFamily: OpenSansFonts.boldItalic };
      if (weight >= 600) return { fontFamily: OpenSansFonts.semiboldItalic };
      if (weight >= 500) return { fontFamily: OpenSansFonts.mediumItalic };
      if (weight >= 400) return { fontFamily: OpenSansFonts.italic };
      return { fontFamily: OpenSansFonts.lightItalic };
    }
    if (weight >= 800) return { fontFamily: OpenSansFonts.extraBold };
    if (weight >= 700) return { fontFamily: OpenSansFonts.bold };
    if (weight >= 600) return { fontFamily: OpenSansFonts.semibold };
    if (weight >= 500) return { fontFamily: OpenSansFonts.medium };
    if (weight >= 400) return { fontFamily: OpenSansFonts.regular };
    return { fontFamily: OpenSansFonts.light };
  }

  // Montserrat is also split across families per weight (e.g. "Montserrat SemiBold"), so it hits
  // the same iOS weight-collapse problem as Open Sans. Its basenames == PostScript names, so we can
  // resolve to the exact per-face name on every platform (incl. iOS) too.
  if (fontFamily.startsWith('Montserrat')) {
    if (isItalic) {
      if (weight >= 900) return { fontFamily: MontserratFonts.blackItalic };
      if (weight >= 800) return { fontFamily: MontserratFonts.extraBoldItalic };
      if (weight >= 700) return { fontFamily: MontserratFonts.boldItalic };
      if (weight >= 600) return { fontFamily: MontserratFonts.semiboldItalic };
      if (weight >= 500) return { fontFamily: MontserratFonts.mediumItalic };
      if (weight >= 400) return { fontFamily: MontserratFonts.italic };
      if (weight >= 300) return { fontFamily: MontserratFonts.lightItalic };
      return { fontFamily: MontserratFonts.thinItalic };
    }
    if (weight >= 900) return { fontFamily: MontserratFonts.black };
    if (weight >= 800) return { fontFamily: MontserratFonts.extraBold };
    if (weight >= 700) return { fontFamily: MontserratFonts.bold };
    if (weight >= 600) return { fontFamily: MontserratFonts.semibold };
    if (weight >= 500) return { fontFamily: MontserratFonts.medium };
    if (weight >= 400) return { fontFamily: MontserratFonts.regular };
    if (weight >= 300) return { fontFamily: MontserratFonts.light };
    return { fontFamily: MontserratFonts.thin };
  }

  // iOS: Inter resolves from the family name + fontWeight via the system. Web has no built-in
  // custom fonts; we load per-face names via expo-font (same keys as Android).
  if (Platform.OS !== 'android' && Platform.OS !== 'web') {
    return { fontFamily };
  }

  // Android / web: Inter is linked per face by file name.
  if (!fontFamily.startsWith('Inter')) {
    return { fontFamily };
  }

  if (isItalic) {
    if (weight >= 900) return { fontFamily: InterFonts.blackItalic };
    if (weight >= 800) return { fontFamily: InterFonts.extraBoldItalic };
    if (weight >= 700) return { fontFamily: InterFonts.boldItalic };
    if (weight >= 600) return { fontFamily: InterFonts.semiboldItalic };
    if (weight >= 500) return { fontFamily: InterFonts.mediumItalic };
    if (weight >= 400) return { fontFamily: InterFonts.italic };
    if (weight >= 300) return { fontFamily: InterFonts.lightItalic };
    return { fontFamily: InterFonts.thinItalic };
  }

  if (weight >= 900) return { fontFamily: InterFonts.black };
  if (weight >= 800) return { fontFamily: InterFonts.extraBold };
  if (weight >= 700) return { fontFamily: InterFonts.bold };
  if (weight >= 600) return { fontFamily: InterFonts.semibold };
  if (weight >= 500) return { fontFamily: InterFonts.medium };
  if (weight >= 400) return { fontFamily: InterFonts.regular };
  if (weight >= 300) return { fontFamily: InterFonts.light };
  return { fontFamily: InterFonts.thin };
};

/**
 * Typography tokens — raw Figma design values (unscaled).
 * Scaling is applied at render time by the Text component via `useScaling()`.
 */
export const typography = {
  'Text Button': {
    fontFamily: fontFamilies.inter,
    fontSize: 16,
    fontWeight: '500' as const,
    lineHeight: 26,
    letterSpacing: 0,
    textTransform: 'uppercase' as const,
  },
  'Text Title Header': {
    fontFamily: fontFamilies.inter,
    fontSize: 18,
    fontWeight: '600' as const,
    lineHeight: 25,
    letterSpacing: 0,
    textTransform: 'uppercase' as const,
  },
  'Text Label Input': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 10,
    fontWeight: '500' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'uppercase' as const,
  },

  'Font Inter/Heading/Heading XXL': {
    fontFamily: fontFamilies.inter,
    fontSize: 48,
    fontWeight: '700' as const,
    lineHeight: 67,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Heading/Heading XL': {
    fontFamily: fontFamilies.inter,
    fontSize: 40,
    fontWeight: '700' as const,
    lineHeight: 56,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Heading/Heading L': {
    fontFamily: fontFamilies.inter,
    fontSize: 32,
    fontWeight: '700' as const,
    lineHeight: 45,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Heading/Heading M': {
    fontFamily: fontFamilies.inter,
    fontSize: 24,
    fontWeight: '700' as const,
    lineHeight: 34,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Heading/Heading S': {
    fontFamily: fontFamilies.inter,
    fontSize: 20,
    fontWeight: '700' as const,
    lineHeight: 28,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },

  'Font Inter/Body/Bold 18px': {
    fontFamily: fontFamilies.inter,
    fontSize: 18,
    fontWeight: '700' as const,
    lineHeight: 25,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Body/Medium 18px': {
    fontFamily: fontFamilies.inter,
    fontSize: 18,
    fontWeight: '600' as const,
    lineHeight: 25,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Body/Regular 18px': {
    fontFamily: fontFamilies.inter,
    fontSize: 18,
    fontWeight: '400' as const,
    lineHeight: 27,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Body/Bold 16px': {
    fontFamily: fontFamilies.inter,
    fontSize: 16,
    fontWeight: '700' as const,
    lineHeight: 26,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Body/Medium 16px': {
    fontFamily: fontFamilies.inter,
    fontSize: 16,
    fontWeight: '500' as const,
    lineHeight: 26,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Body/Regular 16px': {
    fontFamily: fontFamilies.inter,
    fontSize: 16,
    fontWeight: '400' as const,
    lineHeight: 26,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },

  'Font Inter/Subtitle/Bold 14px': {
    fontFamily: fontFamilies.inter,
    fontSize: 14,
    fontWeight: '700' as const,
    lineHeight: 20,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Medium 14px': {
    fontFamily: fontFamilies.inter,
    fontSize: 14,
    fontWeight: '500' as const,
    lineHeight: 20,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Medium 8px': {
    fontFamily: fontFamilies.inter,
    fontSize: 8,
    fontWeight: '500' as const,
    lineHeight: 9.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Regular 14px': {
    fontFamily: fontFamilies.inter,
    fontSize: 14,
    fontWeight: '400' as const,
    lineHeight: 20,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Bold 12px': {
    fontFamily: fontFamilies.inter,
    fontSize: 12,
    fontWeight: '700' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Medium 12px': {
    fontFamily: fontFamilies.inter,
    fontSize: 12,
    fontWeight: '500' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Regular 12px': {
    fontFamily: fontFamilies.inter,
    fontSize: 12,
    fontWeight: '400' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Bold 11px': {
    fontFamily: fontFamilies.inter,
    fontSize: 11,
    fontWeight: '700' as const,
    lineHeight: 16,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Medium 11px': {
    fontFamily: fontFamilies.inter,
    fontSize: 11,
    fontWeight: '500' as const,
    lineHeight: 16,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Regular 11px': {
    fontFamily: fontFamilies.inter,
    fontSize: 11,
    fontWeight: '400' as const,
    lineHeight: 15,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Bold 10px': {
    fontFamily: fontFamilies.inter,
    fontSize: 10,
    fontWeight: '700' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Medium': {
    fontFamily: fontFamilies.inter,
    fontSize: 10,
    fontWeight: '500' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Regular': {
    fontFamily: fontFamilies.inter,
    fontSize: 10,
    fontWeight: '400' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Inter/Subtitle/Italic': {
    fontFamily: fontFamilies.inter,
    fontSize: 10,
    fontWeight: '400' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
    fontStyle: 'italic' as const,
  },

  'Font Montserrat/Heading/Heading XXL': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 48,
    fontWeight: '700' as const,
    lineHeight: 67,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Heading/Heading XL': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 40,
    fontWeight: '700' as const,
    lineHeight: 56,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Heading/Heading L': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 32,
    fontWeight: '700' as const,
    lineHeight: 45,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Heading/Heading M': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 24,
    fontWeight: '700' as const,
    lineHeight: 34,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Heading/Heading S': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 20,
    fontWeight: '700' as const,
    lineHeight: 28,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },

  'Font Montserrat/Body/Body 1 Bold 18px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 18,
    fontWeight: '700' as const,
    lineHeight: 25,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Body/Body 1 SemiBold 18px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 18,
    fontWeight: '600' as const,
    lineHeight: 25,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Body/Body 1 Regular 18px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 18,
    fontWeight: '400' as const,
    lineHeight: 27,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Body/Body 2 Bold 16px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 16,
    fontWeight: '700' as const,
    lineHeight: 26,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Body/Body 2 Semibold 16px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 16,
    fontWeight: '600' as const,
    lineHeight: 26,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Body/Body 2 Regular 16px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 16,
    fontWeight: '400' as const,
    lineHeight: 26,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },

  'Font Montserrat/Subtitle/Subtitle 1 Bold 14px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 14,
    fontWeight: '700' as const,
    lineHeight: 20,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Subtitle 1 Semibold 14px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 14,
    fontWeight: '600' as const,
    lineHeight: 20,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Subtitle 1 Regular 14px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 14,
    fontWeight: '400' as const,
    lineHeight: 20,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Subtitle 2 Bold 12px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 12,
    fontWeight: '700' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Subtitle 2 Medium 12px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 12,
    fontWeight: '600' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Subtitle 2 Regular 12px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 12,
    fontWeight: '400' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Minimize 1 Bold 11px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 11,
    fontWeight: '700' as const,
    lineHeight: 16,
    letterSpacing: 0,
    textTransform: 'uppercase' as const,
  },
  'Font Montserrat/Subtitle/Minimize 1 Semibold 11px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 11,
    fontWeight: '600' as const,
    lineHeight: 16,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Minimize 1 Regular 11px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 11,
    fontWeight: '400' as const,
    lineHeight: 16,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Minimize 2 Bold 10px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 10,
    fontWeight: '700' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Minimize 2 Medium 10px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 10,
    fontWeight: '600' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Minimize 2 Regular 10px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 10,
    fontWeight: '400' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Montserrat/Subtitle/Minimize 2 Italic 10px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 10,
    fontWeight: '400' as const,
    lineHeight: 14,
    letterSpacing: 0,
    textTransform: 'none' as const,
    fontStyle: 'italic' as const,
  },

  // Guardian App (lineHeight = 1.4 ratio from Figma, e.g. 14 × 1.4 = 19.6). Headings use
  // Montserrat; subtitles use Open Sans.
  'Font Guardian App/Heading/Heading H5 24px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 24,
    fontWeight: '700' as const,
    lineHeight: 33.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Heading/Heading H6 18px': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 18,
    fontWeight: '700' as const,
    lineHeight: 25.2,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Body/Body 16 Bold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 16,
    fontWeight: '700' as const,
    lineHeight: 25.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Body/Body 16 Semibold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 16,
    fontWeight: '600' as const,
    lineHeight: 25.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 14 Regular': {
    fontFamily: fontFamilies.openSans,
    fontSize: 14,
    fontWeight: '400' as const,
    lineHeight: 19.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 14 SemiBold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 14,
    fontWeight: '600' as const,
    lineHeight: 19.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 14 Bold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 14,
    fontWeight: '700' as const,
    lineHeight: 19.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 10 Regular': {
    fontFamily: fontFamilies.openSans,
    fontSize: 10,
    fontWeight: '400' as const,
    lineHeight: 12,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 10 Semibold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 10,
    fontWeight: '600' as const,
    lineHeight: 12,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 12 Regular': {
    fontFamily: fontFamilies.openSans,
    fontSize: 12,
    fontWeight: '400' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 12 Semibold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 12,
    fontWeight: '600' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 14 Semibold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 14,
    fontWeight: '600' as const,
    lineHeight: 19.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Body/Body 16px Regular': {
    fontFamily: fontFamilies.openSans,
    fontSize: 16,
    fontWeight: '400' as const,
    lineHeight: 25.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 11 Regular': {
    fontFamily: fontFamilies.openSans,
    fontSize: 11,
    fontWeight: '400' as const,
    lineHeight: 14.3,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 12 Bold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 12,
    fontWeight: '700' as const,
    lineHeight: 16.8,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 14 Italic': {
    fontFamily: fontFamilies.openSans,
    fontSize: 14,
    fontWeight: '400' as const,
    lineHeight: 19.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
    fontStyle: 'italic' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 11 Semibold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 11,
    fontWeight: '600' as const,
    lineHeight: 14.3,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Heading/Heading 24': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 24,
    fontWeight: '800' as const,
    lineHeight: 33.6,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Heading/Heading 20': {
    fontFamily: fontFamilies.montserrat,
    fontSize: 20,
    fontWeight: '700' as const,
    lineHeight: 28,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Inter/Paragraph/X Small': {
    fontFamily: fontFamilies.inter,
    fontSize: 12,
    fontWeight: '400' as const,
    lineHeight: 16,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  'Font Guardian App/Subtitle/Subtitle 10 Bold': {
    fontFamily: fontFamilies.openSans,
    fontSize: 10,
    fontWeight: '700' as const,
    lineHeight: 12,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
} as const;

export type TypographyToken = keyof typeof typography;
